import React, { useState, useEffect, useRef } from 'react';
import { 
  Download,
  TrendingUp,
  Users,
  DollarSign,
  Calendar as CalendarIcon,
  BarChart3,
  PieChart,
  FileText,
  RefreshCw,
  Filter,
  CalendarRange,
  Printer,
  FileDown,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import * as api from '../services/api';
import { format, subDays, subMonths, subYears, startOfDay, endOfDay, isWithinInterval, parseISO, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';

const ReportsPage = ({ currentUser, globalSearch = '' }) => {
  const [loading, setLoading] = useState(true);
  const [pipelineData, setPipelineData] = useState([]);
  const [userPerformance, setUserPerformance] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [deals, setDeals] = useState([]);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [users, setUsers] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [allCommissionConfigs, setAllCommissionConfigs] = useState({});
  
  // Filter state - default to current user for non-admins
  const isAdmin = currentUser?.role === 'administrator' || currentUser?.role === 'admin';
  const [userFilter, setUserFilter] = useState(isAdmin ? 'all' : currentUser?.name || 'all');
  
  // Date range filter state - default to 1 year for better overview
  const [dateRange, setDateRange] = useState('year');
  const [startDate, setStartDate] = useState(subYears(new Date(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  
  // Function to get date range based on preset selection
  const getDateRangeFromPreset = (preset) => {
    const now = new Date();
    switch (preset) {
      case 'week':
        return { start: subDays(now, 7), end: now };
      case 'month':
        return { start: subMonths(now, 1), end: now };
      case 'quarter':
        return { start: subMonths(now, 3), end: now };
      case 'year':
        return { start: subYears(now, 1), end: now };
      case 'all':
        return { start: subYears(now, 5), end: now };
      case 'custom':
        return { start: startDate, end: endDate };
      default:
        return { start: subYears(now, 1), end: now };
    }
  };
  
  // Update dates when preset changes
  useEffect(() => {
    if (dateRange !== 'custom') {
      const now = new Date();
      let range;
      switch (dateRange) {
        case 'week':
          range = { start: subDays(now, 7), end: now };
          break;
        case 'month':
          range = { start: subMonths(now, 1), end: now };
          break;
        case 'quarter':
          range = { start: subMonths(now, 3), end: now };
          break;
        case 'year':
          range = { start: subYears(now, 1), end: now };
          break;
        case 'all':
          range = { start: subYears(now, 5), end: now };
          break;
        default:
          range = { start: subYears(now, 1), end: now };
      }
      setStartDate(range.start);
      setEndDate(range.end);
      setShowCustomDateRange(false);
    } else {
      setShowCustomDateRange(true);
    }
  }, [dateRange]);

  useEffect(() => {
    loadReports();
  }, []);

  // Update filter when currentUser changes
  useEffect(() => {
    if (!isAdmin && currentUser?.name) {
      setUserFilter(currentUser.name);
    }
  }, [currentUser, isAdmin]);

  const loadReports = async () => {
    try {
      setLoading(true);
      // Use Promise.allSettled to handle individual failures
      const results = await Promise.allSettled([
        api.getPipelineValue(),
        api.getPerformanceByUser(),
        api.getConversionByStage(),
        api.getSalesByPeriod('monthly'),
        api.getDeals(),
        api.getEvaluaciones(),
        api.getUsers(),
        api.getPipelines(),
        api.getTasks(),      // Load tasks for overdue calculation
        api.getEmpresas(),   // Load empresas for count
        api.getAllUserCommissions() // Load all commission configs for admin view
      ]);
      
      // Extract data safely from each result
      const [pipelineRes, performanceRes, conversionRes, salesRes, dealsRes, evaluacionesRes, usersRes, pipelinesRes, tasksRes, empresasRes, commissionsRes] = results;
      
      if (pipelineRes.status === 'fulfilled') setPipelineData(pipelineRes.value?.data || pipelineRes.value || []);
      if (performanceRes.status === 'fulfilled') setUserPerformance(performanceRes.value?.data || performanceRes.value || []);
      if (conversionRes.status === 'fulfilled') setConversionData(conversionRes.value?.data || conversionRes.value || []);
      if (salesRes.status === 'fulfilled') setSalesData(salesRes.value?.data || salesRes.value || {});
      
      // Load commission configs into a map by user_id
      if (commissionsRes.status === 'fulfilled') {
        const configs = commissionsRes.value?.data || commissionsRes.value || [];
        const configMap = {};
        configs.forEach(c => {
          configMap[c.user_id] = c;
        });
        setAllCommissionConfigs(configMap);
      }
      
      // Get pipelines first to create lookup map
      let pipelinesData = [];
      if (pipelinesRes.status === 'fulfilled') {
        pipelinesData = pipelinesRes.value?.data || pipelinesRes.value || [];
        setPipelines(Array.isArray(pipelinesData) ? pipelinesData : []);
      }
      
      // Create pipeline ID to name mapping
      const pipelineMap = {};
      pipelinesData.forEach(p => {
        if (p.id) pipelineMap[p.id] = p.name || 'Sin nombre';
      });
      
      // Enrich deals with pipeline_name from the map
      if (dealsRes.status === 'fulfilled') {
        const dealsData = dealsRes.value?.data || dealsRes.value || [];
        const enrichedDeals = (Array.isArray(dealsData) ? dealsData : []).map(deal => ({
          ...deal,
          pipeline_name: deal.pipeline_name || pipelineMap[deal.pipeline_id] || 'Sin Pipeline'
        }));
        setDeals(enrichedDeals);
      }
      
      if (evaluacionesRes.status === 'fulfilled') {
        const evalData = evaluacionesRes.value?.data || evaluacionesRes.value || [];
        setEvaluaciones(Array.isArray(evalData) ? evalData : []);
      }
      if (usersRes.status === 'fulfilled') {
        const usersData = usersRes.value?.data || usersRes.value || [];
        setUsers(Array.isArray(usersData) ? usersData : []);
      }
      if (tasksRes.status === 'fulfilled') {
        const tasksData = tasksRes.value?.data || tasksRes.value || [];
        setTasks(Array.isArray(tasksData) ? tasksData : []);
      }
      if (empresasRes.status === 'fulfilled') {
        const empresasData = empresasRes.value?.data || empresasRes.value || [];
        setEmpresas(Array.isArray(empresasData) ? empresasData : []);
      }
      
      // Log any failed requests for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['pipeline', 'performance', 'conversion', 'sales', 'deals', 'evaluaciones', 'users', 'pipelines', 'tasks', 'empresas', 'commissions'];
          console.warn(`Failed to load ${endpoints[index]}:`, result.reason);
        }
      });
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get commission rate for a user + pipeline
  const getCommissionRateForUserPipeline = (userId, pipelineId, defaultRate = 35) => {
    const userConfig = allCommissionConfigs[userId];
    if (!userConfig) {
      // Find user and use their default rate
      const user = users.find(u => u.id === userId || u.name === userId);
      return user?.commission_rate || defaultRate;
    }
    const pipelineRate = (userConfig.pipeline_rates || []).find(pr => pr.pipeline_id === pipelineId);
    return pipelineRate ? pipelineRate.rate : (userConfig.default_rate || defaultRate);
  };

  // Helper function to get commission rate for a user + service
  const getCommissionRateForUserService = (userId, serviceName, defaultRate = 20) => {
    const userConfig = allCommissionConfigs[userId];
    if (!userConfig) {
      const user = users.find(u => u.id === userId || u.name === userId);
      return user?.commission_rate || defaultRate;
    }
    const serviceRate = (userConfig.service_rates || []).find(
      sr => sr.service_name?.toLowerCase() === serviceName?.toLowerCase()
    );
    return serviceRate ? serviceRate.rate : (userConfig.default_rate || defaultRate);
  };

  // Get user ID from name
  const getUserIdFromName = (userName) => {
    const user = users.find(u => u.name === userName);
    return user?.id || userName;
  };

  // Filter deals and evaluaciones by user
  const filterByDateRange = (items, dateField = 'created_at') => {
    if (!startDate || !endDate) return items;
    
    return items.filter(item => {
      const itemDate = item[dateField] ? parseISO(item[dateField]) : null;
      if (!itemDate) return true; // Include items without dates
      
      try {
        return isWithinInterval(itemDate, {
          start: startOfDay(startDate),
          end: endOfDay(endDate)
        });
      } catch {
        return true; // Include if date parsing fails
      }
    });
  };

  const filteredDeals = filterByDateRange(
    userFilter === 'all' 
      ? deals 
      : deals.filter(d => d.assigned_to === userFilter || d.owner === userFilter)
  );
  
  const filteredEvaluaciones = filterByDateRange(
    userFilter === 'all'
      ? evaluaciones
      : evaluaciones.filter(e => e.assigned_to === userFilter || e.sold_by === userFilter || e.user === userFilter)
  );

  // Filter tasks by user
  const filteredTasks = userFilter === 'all'
    ? tasks
    : tasks.filter(t => t.assigned_to === userFilter);

  // Filter empresas by user
  const filteredEmpresas = userFilter === 'all'
    ? empresas
    : empresas.filter(e => e.assigned_to === userFilter);

  // Calculate overdue tasks (pending tasks with due_date more than 3 days ago)
  const now = new Date();
  const overdueTasks = filteredTasks.filter(t => {
    if (t.status === 'Completada') return false;
    if (!t.due_date) return false;
    try {
      const dueDate = parseISO(t.due_date);
      const daysDiff = differenceInDays(now, dueDate);
      return daysDiff > 3;
    } catch {
      return false;
    }
  });

  // Calculate ALL metrics from filtered data (not from pipelineData)
  // Won deals - either is_won flag or in a won stage
  const wonDeals = filteredDeals.filter(d => d.is_won || d.stage === 'Acuerdo Firmado' || d.stage === 'Ganado' || d.stage === 'Cerrado Ganado');
  const wonValue = wonDeals.reduce((sum, d) => sum + (d.value || 0), 0);
  
  // Lost deals
  const lostDeals = filteredDeals.filter(d => d.is_lost || d.stage === 'Perdido' || d.stage === 'Cerrado Perdido');
  const lostValue = lostDeals.reduce((sum, d) => sum + (d.value || 0), 0);
  
  // Pending deals - not won and not lost
  const pendingDeals = filteredDeals.filter(d => !d.is_won && !d.is_lost && d.stage !== 'Perdido' && d.stage !== 'Cerrado Perdido');
  const pendingValue = pendingDeals.reduce((sum, d) => sum + (d.value || 0), 0);
  
  // Total pipeline value
  const totalPipelineValue = filteredDeals.reduce((sum, d) => sum + (d.value || 0), 0);
  
  // Monthly recurring (FROM WON DEALS ONLY - per user request)
  const monthlyRecurring = wonDeals.reduce((sum, d) => sum + (d.monthly_value || 0), 0);
  
  // Cotizaciones activas vs cerradas
  const activeCotizaciones = filteredEvaluaciones.filter(e => e.status !== 'cerrada' && e.status !== 'vendida');
  const closedCotizaciones = filteredEvaluaciones.filter(e => e.status === 'cerrada' || e.status === 'vendida');
  const totalCotizacionesValue = filteredEvaluaciones.reduce((sum, e) => sum + (e.appraisal_value || 0), 0);
  const activeCotizacionesValue = activeCotizaciones.reduce((sum, e) => sum + (e.appraisal_value || 0), 0);
  const closedCotizacionesValue = closedCotizaciones.reduce((sum, e) => sum + (e.appraisal_value || 0), 0);
  
  // Pipeline + Cotizaciones total value
  const totalPipelinePlusCotizaciones = totalPipelineValue + totalCotizacionesValue;
  
  // Commissions - using custom rates per user/pipeline/service
  const dealsCommission = filteredDeals.reduce((sum, d) => {
    if (d.is_won) {
      // Get user ID from assigned_to or owner
      const userId = getUserIdFromName(d.assigned_to || d.owner);
      const rate = getCommissionRateForUserPipeline(userId, d.pipeline_id, 35);
      return sum + ((d.value || 0) * (rate / 100));
    }
    return sum;
  }, 0);
  
  const cotizacionesCommission = filteredEvaluaciones.reduce((sum, e) => {
    if (e.sales_commission || e.potential_commission) {
      return sum + (e.sales_commission || e.potential_commission);
    }
    // Calculate using custom rate
    const userId = getUserIdFromName(e.assigned_to || e.sold_by || e.user);
    const rate = getCommissionRateForUserService(userId, e.service_name, 20);
    return sum + ((e.appraisal_value || 0) * (rate / 100));
  }, 0);
  const totalCommission = dealsCommission + cotizacionesCommission;
  
  // Conversion rate
  const totalDealsCount = filteredDeals.length;
  const conversionRate = totalDealsCount > 0 ? ((wonDeals.length / totalDealsCount) * 100).toFixed(1) : 0;
  
  // Group deals by pipeline for "Valor por Pipeline" section
  const dealsByPipeline = filteredDeals.reduce((acc, deal) => {
    const pipelineId = deal.pipeline_id || 'default';
    const pipelineName = deal.pipeline_name || 'Sin Pipeline';
    if (!acc[pipelineId]) {
      acc[pipelineId] = {
        pipeline_id: pipelineId,
        pipeline_name: pipelineName,
        deals: [],
        total_value: 0,
        won_value: 0,
        pending_value: 0,
        lost_value: 0,
        won_count: 0,
        lost_count: 0
      };
    }
    acc[pipelineId].deals.push(deal);
    acc[pipelineId].total_value += (deal.value || 0);
    if (deal.is_won) {
      acc[pipelineId].won_value += (deal.value || 0);
      acc[pipelineId].won_count += 1;
    } else if (deal.is_lost) {
      acc[pipelineId].lost_value += (deal.value || 0);
      acc[pipelineId].lost_count += 1;
    } else {
      acc[pipelineId].pending_value += (deal.value || 0);
    }
    return acc;
  }, {});
  const filteredPipelineData = Object.values(dealsByPipeline);

  // Filter conversion data by user (count deals per stage for the selected user)
  const filteredConversionData = conversionData.map(pipeline => {
    // For each pipeline, recalculate stage counts based on filtered deals
    const pipelineDeals = filteredDeals.filter(d => d.pipeline_id === pipeline.pipeline_id);
    const stagesWithCounts = pipeline.stages.map(stage => {
      const stageDeals = pipelineDeals.filter(d => d.stage_id === stage.stage_id || d.stage === stage.stage_name);
      return {
        ...stage,
        deal_count: stageDeals.length
      };
    });
    return {
      ...pipeline,
      stages: stagesWithCounts
    };
  });

  // Filter user performance to show only selected user (or all if 'all')
  const filteredUserPerformance = userFilter === 'all' 
    ? userPerformance 
    : userPerformance.filter(u => u.user_name === userFilter);

  // PDF Download function
  const downloadPDF = () => {
    const userName = userFilter === 'all' ? 'Todos los usuarios' : userFilter;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Por favor habilita las ventanas emergentes para descargar el PDF');
      return;
    }
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Reporte de Ventas - ${userName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #D4A843; }
            h2 { color: #333; margin-top: 30px; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
            .card { border: 1px solid #ddd; border-radius: 8px; padding: 15px; }
            .card-title { font-size: 14px; color: #666; }
            .card-value { font-size: 24px; font-weight: bold; margin: 10px 0; }
            .green { color: #22c55e; }
            .blue { color: #3b82f6; }
            .yellow { color: #D4A843; }
            .purple { color: #9333ea; }
            .red { color: #ef4444; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f5f5f5; }
            .summary { margin-top: 30px; padding: 20px; background: #f9f9f9; border-radius: 8px; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <h1>Reporte de Ventas</h1>
          <p><strong>Usuario:</strong> ${userName}</p>
          <p><strong>Período:</strong> ${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}</p>
          <p><strong>Generado:</strong> ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
          
          <h2>Resumen General</h2>
          <div class="grid">
            <div class="card">
              <div class="card-title">Empresas Asignadas</div>
              <div class="card-value">${filteredEmpresas.length}</div>
            </div>
            <div class="card">
              <div class="card-title">Tareas Atrasadas</div>
              <div class="card-value red">${overdueTasks.length}</div>
              <small>Más de 3 días sin completar</small>
            </div>
            <div class="card">
              <div class="card-title">Ventas Cerradas (Período)</div>
              <div class="card-value green">$${wonValue.toLocaleString()}</div>
              <small>${wonDeals.length} deals ganados</small>
            </div>
          </div>
          
          <div class="grid" style="margin-top: 20px;">
            <div class="card">
              <div class="card-title">Total Deals</div>
              <div class="card-value">${totalDealsCount}</div>
              <small class="green">✓ ${wonDeals.length} ganados</small> | 
              <small class="red">✗ ${lostDeals.length} perdidos</small>
            </div>
            <div class="card">
              <div class="card-title">Pipeline + Cotizaciones</div>
              <div class="card-value yellow">$${totalPipelinePlusCotizaciones.toLocaleString()}</div>
              <small>Pipeline: $${totalPipelineValue.toLocaleString()} | Cotiz: $${totalCotizacionesValue.toLocaleString()}</small>
            </div>
            <div class="card">
              <div class="card-title">Ingresos Recurrentes (Ganados)</div>
              <div class="card-value purple">$${monthlyRecurring.toLocaleString()}/mes</div>
              <small>$${(monthlyRecurring * 12).toLocaleString()}/año</small>
            </div>
          </div>
          
          <h2>Valor por Pipeline</h2>
          <table>
            <tr>
              <th>Pipeline</th>
              <th>Deals</th>
              <th>Valor Total</th>
              <th>Ganado</th>
              <th>Pendiente</th>
            </tr>
            ${filteredPipelineData.map(p => `
              <tr>
                <td>${p.pipeline_name}</td>
                <td>${p.deals.length}</td>
                <td>$${p.total_value.toLocaleString()}</td>
                <td class="green">$${p.won_value.toLocaleString()}</td>
                <td class="blue">$${p.pending_value.toLocaleString()}</td>
              </tr>
            `).join('')}
          </table>
          
          ${userFilter !== 'all' ? `
          <h2>Rendimiento de ${userFilter}</h2>
          <table>
            <tr>
              <th>Métrica</th>
              <th>Valor</th>
            </tr>
            <tr><td>Empresas Asignadas</td><td>${filteredEmpresas.length}</td></tr>
            <tr><td>Total Deals</td><td>${totalDealsCount}</td></tr>
            <tr><td>Deals Ganados</td><td>${wonDeals.length}</td></tr>
            <tr><td>Tasa de Conversión</td><td>${conversionRate}%</td></tr>
            <tr><td>Valor Ganado</td><td class="green">$${wonValue.toLocaleString()}</td></tr>
            <tr><td>Ingresos Recurrentes</td><td class="purple">$${monthlyRecurring.toLocaleString()}/mes</td></tr>
            <tr><td>Comisión Potencial</td><td class="yellow">$${totalCommission.toLocaleString()}</td></tr>
          </table>
          ` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Reportes de Ventas</h1>
          <p className="text-sm text-gray-500">
            {userFilter === 'all' ? 'Reporte Global' : `Reporte de ${userFilter}`}
            {startDate && endDate && ` • ${format(startDate, 'dd MMM', { locale: es })} - ${format(endDate, 'dd MMM yyyy', { locale: es })}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && (
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Filtrar por usuario" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los usuarios</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]" data-testid="date-range-select">
              <CalendarIcon size={16} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el Tiempo</SelectItem>
              <SelectItem value="year">Último Año</SelectItem>
              <SelectItem value="quarter">Último Trimestre</SelectItem>
              <SelectItem value="month">Último Mes</SelectItem>
              <SelectItem value="week">Última Semana</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadReports}>
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
          <Button variant="outline" onClick={downloadPDF} className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100">
            <FileDown size={16} className="mr-2" />
            Descargar PDF
          </Button>
        </div>
      </div>

      {/* NEW: Top Summary Cards - Empresas, Tareas Atrasadas, Ventas Cerradas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center mb-2">
              <Building2 className="text-blue-500 mr-2" size={20} />
              <p className="text-sm text-gray-500">Empresas Asignadas</p>
            </div>
            <p className="text-3xl font-bold text-blue-600">{filteredEmpresas.length}</p>
            <p className="text-sm text-gray-500 mt-2">
              {userFilter === 'all' ? 'Total en el CRM' : `Asignadas a ${userFilter}`}
            </p>
          </CardContent>
        </Card>
        
        <Card className={`bg-gradient-to-br ${overdueTasks.length > 0 ? 'from-red-50 to-white border-red-200' : 'from-green-50 to-white border-green-200'} border-2`}>
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center mb-2">
              <AlertTriangle className={overdueTasks.length > 0 ? 'text-red-500' : 'text-green-500'} size={20} />
              <p className="text-sm text-gray-500 ml-2">Tareas Atrasadas</p>
            </div>
            <p className={`text-3xl font-bold ${overdueTasks.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {overdueTasks.length}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Más de 3 días sin completar
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center mb-2">
              <DollarSign className="text-green-500 mr-2" size={20} />
              <p className="text-sm text-gray-500">Ventas Cerradas</p>
            </div>
            <p className="text-3xl font-bold text-green-600">${wonValue.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mt-2">
              {wonDeals.length} deals en el período
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" id="reports-content">
        <Card className="bg-gradient-to-br from-gray-50 to-white border-2">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-gray-500 mb-2">Total Deals</p>
            <p className="text-3xl font-bold text-gray-800">{totalDealsCount}</p>
            <div className="flex justify-center space-x-4 mt-3 text-sm">
              <span className="text-green-500">✓ {wonDeals.length} ganados</span>
              <span className="text-red-500">✗ {lostDeals.length} perdidos</span>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-gray-500 mb-2">Pipeline + Cotizaciones</p>
            <p className="text-3xl font-bold text-[#D4A843]">
              ${totalPipelinePlusCotizaciones.toLocaleString()}
            </p>
            <div className="text-xs text-gray-500 mt-2">
              Pipeline: ${totalPipelineValue.toLocaleString()} | Cotiz: ${totalCotizacionesValue.toLocaleString()}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${(wonValue / Math.max(totalPipelinePlusCotizaciones, 1)) * 100}%` }}
              />
            </div>
            <p className="text-sm text-green-500 mt-2">{((wonValue / Math.max(totalPipelinePlusCotizaciones, 1)) * 100).toFixed(1)}% cerrado</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-gray-500 mb-2">Ingresos Recurrentes (Ganados)</p>
            <p className="text-3xl font-bold text-purple-600">${monthlyRecurring.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mt-2">mensual de deals cerrados</p>
            <p className="text-lg font-semibold text-gray-600 mt-1">${(monthlyRecurring * 12).toLocaleString()}/año</p>
          </CardContent>
        </Card>
      </div>

      {/* Custom Date Range Picker */}
      {showCustomDateRange && (
        <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <CalendarRange size={18} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Rango de fechas:</span>
          </div>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[180px] justify-start text-left font-normal" data-testid="start-date-picker">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, 'dd MMM yyyy', { locale: es }) : 'Fecha inicio'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
                initialFocus
                locale={es}
              />
            </PopoverContent>
          </Popover>
          
          <span className="text-gray-500">hasta</span>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[180px] justify-start text-left font-normal" data-testid="end-date-picker">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, 'dd MMM yyyy', { locale: es }) : 'Fecha fin'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && setEndDate(date)}
                initialFocus
                locale={es}
                disabled={(date) => date < startDate}
              />
            </PopoverContent>
          </Popover>
          
          <Button 
            variant="default" 
            size="sm"
            className="bg-[#D4A843] hover:bg-[#c49a3a]"
            onClick={() => {
              // Trigger re-filter
              loadReports();
            }}
            data-testid="apply-date-filter"
          >
            Aplicar filtro
          </Button>
        </div>
      )}

      {/* User-Specific KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Deals Ganados</p>
                <p className="text-2xl font-bold text-green-600">${wonValue.toLocaleString()}</p>
                <p className="text-xs text-gray-500">{wonDeals.length} deals cerrados</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="text-green-500" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Pipeline Activo</p>
                <p className="text-2xl font-bold text-blue-600">${pendingValue.toLocaleString()}</p>
                <p className="text-xs text-gray-500">{pendingDeals.length} deals pendientes</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-blue-500" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-[#D4A843]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Cotizaciones</p>
                <p className="text-2xl font-bold text-[#D4A843]">${totalCotizacionesValue.toLocaleString()}</p>
                <p className="text-xs text-gray-500">{filteredEvaluaciones.length} cotizaciones</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FileText className="text-[#D4A843]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Comisiones Potenciales</p>
                <p className="text-2xl font-bold text-purple-600">${totalCommission.toLocaleString()}</p>
                <p className="text-xs text-gray-500">De cotizaciones y deals</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="text-purple-500" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cotizaciones Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="text-[#D4A843]" size={20} />
            Desglose de Cotizaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium flex items-center gap-2">
                    <Clock size={16} />
                    Cotizaciones Activas
                  </p>
                  <p className="text-2xl font-bold text-blue-700 mt-1">{activeCotizaciones.length}</p>
                  <p className="text-lg text-blue-600">${activeCotizacionesValue.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium flex items-center gap-2">
                    <CheckCircle size={16} />
                    Cotizaciones Cerradas/Vendidas
                  </p>
                  <p className="text-2xl font-bold text-green-700 mt-1">{closedCotizaciones.length}</p>
                  <p className="text-lg text-green-600">${closedCotizacionesValue.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Value Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-700 flex items-center">
              <BarChart3 size={20} className="mr-2 text-[#D4A843]" />
              Valor por Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredPipelineData.map((pipeline) => (
                <div key={pipeline.pipeline_id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{pipeline.pipeline_name}</span>
                    <span className="text-sm text-gray-500">${pipeline.total_value.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="flex h-3 rounded-full overflow-hidden">
                      <div 
                        className="bg-green-500" 
                        style={{ width: `${(pipeline.won_value / Math.max(pipeline.total_value, 1)) * 100}%` }}
                        title={`Ganado: $${pipeline.won_value}`}
                      />
                      <div 
                        className="bg-blue-400" 
                        style={{ width: `${(pipeline.pending_value / Math.max(pipeline.total_value, 1)) * 100}%` }}
                        title={`Pendiente: $${pipeline.pending_value}`}
                      />
                      <div 
                        className="bg-red-400" 
                        style={{ width: `${(pipeline.lost_value / Math.max(pipeline.total_value, 1)) * 100}%` }}
                        title={`Perdido: $${pipeline.lost_value}`}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>✓ ${pipeline.won_value.toLocaleString()} ganado ({pipeline.won_count})</span>
                    <span>⏳ ${pipeline.pending_value.toLocaleString()} pendiente</span>
                    <span>✗ ${pipeline.lost_value.toLocaleString()} perdido ({pipeline.lost_count})</span>
                  </div>
                </div>
              ))}
              {filteredPipelineData.length === 0 && (
                <p className="text-center text-gray-500 py-4">No hay datos de pipeline para este filtro</p>
              )}
            </div>
            <div className="flex items-center justify-center space-x-4 mt-4 pt-4 border-t">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-xs">Ganado</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-400 rounded-full mr-2"></div>
                <span className="text-xs">Pendiente</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-400 rounded-full mr-2"></div>
                <span className="text-xs">Perdido</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conversion by Stage - NOW FILTERED BY USER */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-700 flex items-center">
              <PieChart size={20} className="mr-2 text-[#D4A843]" />
              Conversión por Etapa {userFilter !== 'all' && `(${userFilter})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {filteredConversionData.map((pipeline, pIndex) => (
                <div key={pipeline.pipeline_id || pIndex} className="space-y-2">
                  <h4 className="font-medium text-gray-700">{pipeline.pipeline_name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {pipeline.stages.map((stage, sIndex) => (
                      <div 
                        key={stage.stage_id || sIndex} 
                        className="px-3 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: stage.color || '#6b7280' }}
                      >
                        {stage.stage_name}: {stage.deal_count}
                        {stage.is_won_stage && ' ✓'}
                        {stage.is_lost_stage && ' ✗'}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {filteredConversionData.length === 0 && (
                <p className="text-center text-gray-500 py-4">No hay datos de conversión para este filtro</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance by User - NOW SHOWS ONLY SELECTED USER OR ALL */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-700 flex items-center">
            <Users size={20} className="mr-2 text-[#D4A843]" />
            {userFilter === 'all' ? 'Rendimiento por Vendedor' : `Rendimiento de ${userFilter}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-medium text-gray-600">Vendedor</th>
                  <th className="pb-3 font-medium text-gray-600">Empresas</th>
                  <th className="pb-3 font-medium text-gray-600">Deals</th>
                  <th className="pb-3 font-medium text-gray-600">Ganados</th>
                  <th className="pb-3 font-medium text-gray-600">Conversión</th>
                  <th className="pb-3 font-medium text-gray-600">Valor Ganado</th>
                  <th className="pb-3 font-medium text-gray-600">Comisión</th>
                  <th className="pb-3 font-medium text-gray-600">Tareas</th>
                </tr>
              </thead>
              <tbody>
                {filteredUserPerformance.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-8 text-center text-gray-500">
                      No hay datos de rendimiento para este usuario
                    </td>
                  </tr>
                ) : (
                  filteredUserPerformance.map((user) => (
                    <tr key={user.user_id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3">
                        <div>
                          <p className="font-medium text-gray-800">{user.user_name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="py-3 text-gray-600">{user.empresas_assigned}</td>
                      <td className="py-3 text-gray-600">{user.total_deals}</td>
                      <td className="py-3">
                        <span className="text-green-600 font-medium">{user.won_deals}</span>
                        <span className="text-gray-400"> / </span>
                        <span className="text-red-600">{user.lost_deals}</span>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-[#D4A843] h-2 rounded-full" 
                              style={{ width: `${Math.min(user.conversion_rate, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{user.conversion_rate.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="py-3 font-medium text-green-600">${user.total_won_value.toLocaleString()}</td>
                      <td className="py-3 text-[#D4A843] font-medium">
                        ${user.potential_commission.toFixed(0)}
                        <span className="text-xs text-gray-400 ml-1">({user.commission_rate}%)</span>
                      </td>
                      <td className="py-3 text-gray-600">
                        {user.completed_tasks}/{user.total_tasks}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
